import { SurveyAnswer, SurveyAttachedFile } from "@graffer-inc/cloud-gov-core";
import {
  FormDataConverter,
  FormSchema,
  isInputClassElement,
} from "@graffer-inc/form-schema";
import { FileType, FolderType } from "@graffer-inc/smart-apply";
import apiClient from "../../../apis/api-client";
import { getFileNameFromContentDisposition } from "../../../../utils/header-utils";
import { isHeicOrHeif, isJPG } from "../../../../utils/file-utils";

export type SurveyMetadataFileType = {
  answerId: string;
  file: SurveyAttachedFile;
};

export type SurveyMetadataFolderType = {
  name: string;
  items: SurveyMetadataFileType[];
};

type folderType = {
  folderName: string;
  formId: string;
};

export const generateAttachedFilesMetadata = (
  formSchema: FormSchema,
  results: SurveyAnswer[],
): SurveyMetadataFolderType[] => {
  const flatten = FormSchema.fromJSON(formSchema)
    .flattenDeep()
    .filter(isInputClassElement);

  const surveyAnswersWithElement = results.map((a) => ({
    ...a,
    formData: new FormDataConverter(formSchema, a.answer).flattenWithElement(),
  }));

  const resutlFolders: SurveyMetadataFolderType[] = [];
  const onlyFileFolders: folderType[] = flatten
    .filter((form) => form.inputType === "file")
    .map((form) => {
      const folderName =
        form.label.text === "" ? "項目名なし" : form.label.text;
      const suffix = form.parent ? form.parent.elemIndex + 1 : "";

      return {
        folderName: `${folderName}${suffix}`,
        formId: form.formFieldId,
      };
    });

  for (const folder of onlyFileFolders) {
    const resultFolder: SurveyMetadataFolderType = {
      name: generateNonDuplicateFolderName(folder.folderName, resutlFolders),
      items: [],
    };
    for (const s of surveyAnswersWithElement) {
      const formData = s.formData[folder.formId];
      // 繰り返し項目で、回答側で繰り返しが行われなかった場合、formDataがundefinedになる
      if (!formData || !formData.value) continue;
      const file = s.files.find((file) => file.refKey === formData.value);
      if (!file) continue;
      resultFolder.items.push({
        answerId: s.id,
        file: file,
      });
    }
    resutlFolders.push(resultFolder);
  }
  return resutlFolders;
};

const generateNonDuplicateFolderName = (
  label: string,
  folders: SurveyMetadataFolderType[],
  suffixNumber = 0,
): string => {
  const folderName = `${label}${
    suffixNumber === 0 ? "" : `（${suffixNumber}）`
  }`;
  if (folders.some((folder) => folder.name === folderName)) {
    return generateNonDuplicateFolderName(label, folders, suffixNumber + 1);
  }
  return folderName;
};

const fetchFile = async (
  fileMetadata: SurveyMetadataFileType,
): Promise<FileType> => {
  const fileUri = encodeURI(
    `/survey-answers/${fileMetadata.answerId}/attached-files/${fileMetadata.file.refKey}`,
  );
  const res = await apiClient.fetchAttachedFile(fileUri);
  const fileName = getFileNameFromContentDisposition(
    res.headers["content-disposition"]
      ? res.headers["content-disposition"]
      : "",
  );

  // LGWANからheic/heifファイルをダウンロードする際は、heic-converterによってjpgに変換されるため、フロントエンドでもファイル名を変更する
  // 基本的にはDBに保存されたファイル名をそのまま利用したいので、content-dispositionのファイル名を利用するのではなく、拡張子が違うときだけフロントエンドで拡張子を付け足す
  // ref. https://graffer.esa.io/posts/2585#heic%E3%80%81heif%E3%83%95%E3%82%A1%E3%82%A4%E3%83%AB%E3%81%8C%E3%83%80%E3%82%A6%E3%83%B3%E3%83%AD%E3%83%BC%E3%83%89%E3%81%95%E3%82%8C%E3%82%8B%E4%BB%95%E7%B5%84%E3%81%BF
  return {
    name:
      isHeicOrHeif(fileMetadata.file.name) && isJPG(fileName)
        ? // ファイル単体でheic. heifを変換する場合は、バックエンドで"jpg"の拡張子を付与しているので、それに習ってフロントエンドでも"jpg"の拡張子を付与する
          // ref. https://github.com/graffer-inc/cloud-gov/blob/b6ff9676b67f2a6f4b813acc7dcbe0759e8bbe80/backend/src/application/usecases/get_permanently_stored_survey_file.go#L83
          `${fileMetadata.file.name}.jpg`
        : fileMetadata.file.name,
    blob: res.data,
  };
};

const convertToActualData = async (
  metadata: SurveyMetadataFolderType[],
): Promise<FolderType[]> => {
  const folders: FolderType[] = [];
  for (const folder of metadata) {
    const newFolder: FolderType = {
      name: folder.name,
      items: [],
    };
    for (const item of folder.items) {
      const file = await fetchFile(item);
      newFolder.items.push(file);
    }
    folders.push(newFolder);
  }
  return folders;
};

export const generateAttachedFilesFolder = async (
  formSchema: FormSchema,
  results: SurveyAnswer[],
): Promise<FolderType[]> => {
  const metadata = generateAttachedFilesMetadata(formSchema, results);
  return await convertToActualData(metadata);
};
